/**
 * The last stage: name, slug and description, filled in from the steps by a suggestion, beside the
 * tool as the AI will see it.
 */

import { Loader2, Sparkles } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { ToolDetails } from '@/modules/back-testing/datasets/tools/tool-details';
import { Field, FieldInput } from '@/modules/simulator/ui/field';
import { PillButton } from '@/modules/simulator/ui/pill-button';
import { draftToolAction } from '../draft-tool-action';
import { LIMITS, slugFormatProblem } from '../custom-tool-draft';
import type { SlugStatus } from '../use-custom-tools';
import { Note } from '../ui/note';
import { useCustomToolBuilder } from './builder-context';

const SLUG_STATUS: Record<Exclude<SlugStatus, 'idle'>, { text: string; bad: boolean }> = {
	checking: { text: 'Checking', bad: false },
	available: { text: 'Available', bad: false },
	failed: { text: 'Could not check the slug', bad: true },
	taken: { text: 'This slug is taken', bad: true },
	invalid: { text: 'Not a valid slug', bad: true },
	tool: { text: 'An existing tool already has this name', bad: true },
	custom_tool: { text: 'Another custom tool uses this slug', bad: true }
};

const Hint = ({ bad, children }: { bad?: boolean; children: string }) => (
	<p className={cn('text-[11px] leading-4', bad ? 'text-red-600' : 'text-gray-800')}>{children}</p>
);

const SlugHint = () => {
	const { draft, toolId, slugStatus, save } = useCustomToolBuilder();
	if (toolId) return <Hint>Protocols call the tool by its slug, so it cannot change.</Hint>;
	if (save.slugError) return <Hint bad>{save.slugError}</Hint>;
	const formatProblem = draft.slug ? slugFormatProblem(draft.slug) : null;
	if (formatProblem) return <Hint bad>{formatProblem}</Hint>;
	if (slugStatus === 'idle') return <Hint>How protocols and the AI refer to the tool.</Hint>;
	const { text, bad } = SLUG_STATUS[slugStatus];
	return <Hint bad={bad}>{text}</Hint>;
};

const NameFields = () => {
	const { draft, change, toolId, suggestion } = useCustomToolBuilder();
	const writing = suggestion.pending && !draft.name.trim();
	const note = writing ? 'Writing a name from your steps' : suggestion.succeeded ? 'Suggested from your steps. Change anything.' : null;
	return (
		<section className='flex flex-col gap-4'>
			<div className='flex items-start justify-between gap-3'>
				<div className='flex flex-col'>
					<h2 className='text-[13px] leading-4.5 font-medium text-text-heading'>Name your tool</h2>
					{note && <p className='text-xs leading-4 text-gray-800'>{note}</p>}
				</div>
				<PillButton onClick={suggestion.ask} disabled={suggestion.pending}>
					{suggestion.pending ? <Loader2 className='size-3.5 animate-spin' /> : <Sparkles className='size-3.5' />}
					Suggest again
				</PillButton>
			</div>
			<Field label='Name'>
				<FieldInput
					value={draft.name}
					maxLength={LIMITS.name}
					placeholder={writing ? 'Writing' : 'Get order status from Shopify and Unicommerce'}
					onChange={(event) => change((current) => ({ ...current, name: event.target.value }))}
				/>
			</Field>
			<div className='flex flex-col gap-1'>
				<Field label='Slug'>
					<FieldInput
						value={draft.slug}
						readOnly={!!toolId}
						placeholder='get_order_status'
						className={cn('font-mono', toolId && 'bg-gray-50 text-gray-900')}
						onChange={(event) => change((current) => ({ ...current, slug: event.target.value }))}
					/>
				</Field>
				<SlugHint />
			</div>
			<Field label='Description, read by the AI'>
				<Textarea
					value={draft.description}
					maxLength={LIMITS.description}
					placeholder='What the tool returns and when to use it'
					className='min-h-28 rounded-lg border-gray-500 text-[13px]'
					onChange={(event) => change((current) => ({ ...current, description: event.target.value }))}
				/>
			</Field>
			{suggestion.failed && <Hint bad>Could not suggest a name. Try again, or write one.</Hint>}
		</section>
	);
};

const Preview = () => {
	const { draft, stepTools, writes } = useCustomToolBuilder();
	const steps = `${draft.steps.length} ${draft.steps.length === 1 ? 'step' : 'steps'}, run as one tool`;
	return (
		<section className='flex flex-col gap-3'>
			<div className='flex flex-col'>
				<h2 className='text-[13px] leading-4.5 font-medium text-text-heading'>How the AI sees it</h2>
				<p className='text-xs leading-4 text-gray-800'>{steps}</p>
			</div>
			<div className='overflow-hidden rounded-xl border border-gray-500'>
				<ToolDetails tool={draftToolAction(draft, stepTools, writes)} />
			</div>
			{writes && <Note>A step changes data, so the whole tool counts as a write. The AI asks for confirmation before it runs.</Note>}
		</section>
	);
};

export const DetailsStage = () => (
	<div className='min-h-0 flex-1 overflow-y-auto bg-white'>
		<div className='mx-auto grid max-w-5xl grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-10 px-8 py-8'>
			<NameFields />
			<Preview />
		</div>
	</div>
);
